/* eslint-disable react-hooks/rules-of-hooks */
import { mountUrl } from '@acme/common'
import Cookies from 'js-cookie'
import _omit from 'lodash.omit'
import { queryClient } from 'providers/ReactQuery'
import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from 'react-query'
import { ErrorHandler } from '.'
import { axiosInstance } from './axios'
import {
    DownloadData,
    IApiService,
    ID,
    Mutations,
    PaginatedResponse,
    PostData,
    QueryKey,
    QueryParams,
    Records,
    UpdateData,
    UpdateDataWithId,
    UseFetchOptions,
    UseInfiniteQueryOptions,
} from './types'

/**
 * Represents an API service that provides methods for making HTTP requests to a specific endpoint.
 *
 * @template TInput - The type of the input data.
 * @template TOutput - The type of the output data.
 * @template TParams - The type of the query parameters.
 */
export default class ApiService<
    TInput = Records,
    TOutput = TInput,
    TParams = QueryParams,
> implements IApiService<TInput, TOutput, TParams>
{
    public readonly url: string
    public mutations: Mutations<TInput, TOutput, TParams> = {
        useCreate: options => {
            const queryClient = useQueryClient()

            return useMutation(
                variables =>
                    axiosInstance.post(
                        mountUrl(this.url, options?.queryParams || {}),
                        variables,
                    ),
                {
                    ..._omit(options, [
                        'version',
                        'invalidateQuery',
                        'queryParams',
                    ]),
                    onSuccess: async (data, variables, context) => {
                        const queryKey = this.queryKey(options?.queryParams)

                        if (options?.onSuccess) {
                            options.onSuccess(data, variables as any, context)
                        }

                        if (options?.invalidateQuery) {
                            await queryClient.invalidateQueries(queryKey)
                        }
                    },
                    onError: options?.onError,
                },
            )
        },
        useImport: options => {
            const queryClient = useQueryClient()

            return useMutation(
                variables =>
                    axiosInstance.post(
                        mountUrl(
                            `${this.url}/import`,
                            options?.queryParams || {},
                        ),
                        variables,
                    ),
                {
                    ..._omit(options, [
                        'version',
                        'invalidateQuery',
                        'queryParams',
                    ]),
                    onSuccess: async (data, variables, context) => {
                        const queryKey = this.queryKey(options?.queryParams)

                        if (options?.onSuccess) {
                            options.onSuccess(data, variables as any, context)
                        }

                        if (options?.invalidateQuery) {
                            await queryClient.invalidateQueries(queryKey)
                        }
                    },
                    onError: options?.onError,
                },
            )
        },
        useUpdate: options => {
            const queryClient = useQueryClient()

            return useMutation(
                ({ id, ...data }) =>
                    axiosInstance.patch(
                        mountUrl(
                            `${this.url}/${id}`,
                            options?.queryParams || {},
                        ),
                        { id, ...data },
                    ),
                {
                    ..._omit(options, [
                        'version',
                        'invalidateQuery',
                        'queryParams',
                    ]),
                    onSuccess: async (data, variables, context) => {
                        const queryKey = this.queryKey(options?.queryParams)

                        if (options?.onSuccess) {
                            options.onSuccess(data, variables as any, context)
                        }

                        if (options?.invalidateQuery) {
                            await queryClient.invalidateQueries(queryKey)
                        }
                    },
                    onError: options?.onError,
                },
            )
        },
        useDelete: options => {
            const queryClient = useQueryClient()

            return useMutation<void, ErrorHandler, number, TParams>(
                id =>
                    axiosInstance.delete(
                        mountUrl(
                            `${this.url}/${id}`,
                            options?.queryParams || {},
                        ),
                    ),
                {
                    onSuccess: async (data, variables, context) => {
                        const queryKey = this.queryKey(options?.queryParams)

                        if (options?.onSuccess) {
                            options.onSuccess(data, variables as any, context)
                        }

                        if (options?.invalidateQuery) {
                            await queryClient.invalidateQueries(queryKey)
                        }
                    },
                    onError: options?.onError,
                },
            )
        },
        useSoftDelete: options => {
            const queryClient = useQueryClient()

            return useMutation<
                void,
                ErrorHandler,
                UpdateDataWithId<TInput>,
                TParams
            >(
                data =>
                    axiosInstance.put(
                        mountUrl(
                            `${this.url}/${data.id}/soft-delete`,
                            options?.queryParams || {},
                        ),
                        data,
                    ),
                {
                    onSuccess: async (data, variables, context) => {
                        const queryKey = this.queryKey(options?.queryParams)

                        if (options?.onSuccess) {
                            options.onSuccess(data, variables as any, context)
                        }

                        if (options?.invalidateQuery) {
                            await queryClient.invalidateQueries(queryKey)
                        }
                    },
                    onError: options?.onError,
                },
            )
        },
    }
    private readonly endpoint: string
    private readonly versionPath: string
    private readonly options: UseFetchOptions<TOutput, TParams>

    constructor(endpoint: string, versionPath?: string)

    constructor(endpoint: string, options: UseFetchOptions<TOutput, TParams>)

    constructor(
        endpoint: string,
        versionPath?: string,
        options?: UseFetchOptions<TOutput, TParams>,
    )

    constructor(
        arg1: string,
        arg2?: string | UseFetchOptions<TOutput, TParams>,
        arg3?: UseFetchOptions<TOutput, TParams>,
    ) {
        this.endpoint = arg1
        this.versionPath = (typeof arg2 === 'string' && arg2) || 'v1'
        this.url = `/${this.versionPath}/${arg1}`

        this.options = (typeof arg2 === 'object' && arg2) || arg3 || {}
    }

    public get URI() {
        const url = new URL(
            this.url,
            new URL(axiosInstance.defaults.baseURL || ''),
        )
        url.protocol = window.location.protocol
        return url.toString()
    }

    /**
     * Returns the query key for the API service.
     * @param query - The query parameters.
     * @returns The query key.
     */
    public queryKey(query = {} as TParams): QueryKey<TParams> {
        return [this.endpoint, this.versionPath, query]
    }

    /**
     * Retrieves query data from the query client.
     * @template T - The type of the query data.
     * @param query - The query parameters.
     * @returns The query data of type T, or undefined if not found.
     */
    public getQueryData<T = TOutput[]>(
        query = {} as TParams & { id?: ID },
    ): T | undefined {
        return queryClient.getQueryData(this.queryKey(query))
    }

    /**
     * Sets the query data for a given query key and updates it using the provided updater function.
     *
     * @template T - The type of the data being updated.
     * @param query - The query parameters, including an optional `id`.
     * @param updater - A function that takes the old data and returns the updated data.
     * @returns The updated query data.
     */
    public setQueryData<T = TOutput[]>(
        query = {} as TParams & { id?: ID },
        updater: (oldData: T | undefined) => T,
    ) {
        return queryClient.setQueryData(this.queryKey(query), updater)
    }

    /**
     * Invalidates the queries associated with the given parameters.
     * @param query - The query parameters.
     * @returns A Promise that resolves when the queries are invalidated.
     */
    public async invalidateQueries(
        query = {} as TParams & { id?: ID },
    ): Promise<void> {
        await queryClient.invalidateQueries(this.queryKey(query))
    }

    /**
     * Deletes a resource by its ID.
     * @param {ID} id - The ID of the resource to delete.
     * @returns {Promise<void>} A promise that resolves when the resource is successfully deleted.
     */
    public delete(id: ID): Promise<void>

    /**
     * Deletes a resource by its ID and prefix.
     * @param {ID} [id] - The ID of the resource to delete.
     * @param {String} [sufixo]
     * @returns {Promise<void>}
     */
    public delete(id: ID, sufixo: string): Promise<void>

    public delete(id: ID, sufixo?: string): Promise<void> {
        if (sufixo) return this.delete(`${id}/${sufixo}`)

        return axiosInstance.delete(`${this.url}/${id}`)
    }

    /**
     * Sends a GET request to the specified endpoint with the provided query parameters.
     * @param {string} [suffix] - The endpoint to send the request to.
     * @param {QueryParams} [params] - The query parameters to be sent in the request.
     * @returns {Promise<T>} A promise that resolves to the response data.
     */
    public get<T = TOutput[]>(suffix?: string, params?: QueryParams): Promise<T>

    /**
     * Sends a GET request to the specified endpoint with the provided query parameters.
     * @param {QueryParams} [params] - The query parameters to be sent in the request.
     * @returns {Promise<T>} A promise that resolves to the response data.
     */
    public get<T = TOutput[]>(params?: QueryParams): Promise<T>

    public get<T = TOutput[]>(
        arg1?: string | QueryParams,
        arg2?: QueryParams,
    ): Promise<T> {
        if (typeof arg1 === 'string') {
            const url = mountUrl(
                `${this.url}${arg1.startsWith('/') ? '' : '/'}${arg1}`,
                arg2 || {},
            )

            return axiosInstance.get(url)
        }

        const url = mountUrl(this.url, arg1 || {})

        return axiosInstance.get(url)
    }

    /**
     * Retrieves a resource by its ID.
     * @param {ID} id - The ID of the resource to retrieve.
     * @returns {Promise<TOutput>} A promise that resolves to the retrieved resource.
     */
    public getById(id: ID): Promise<TOutput> {
        return axiosInstance.get(`${this.url}/${id}`)
    }

    /**
     * Sends a PATCH request to update a resource by its ID.
     *
     * @param {ID} id - The ID of the resource to update.
     * @param {UpdateData<TOutput>} data - The data to be updated.
     * @returns {Promise<TOutput>} A promise that resolves to the updated resource.
     */
    public patch<T = TOutput>(id: ID, data: UpdateData<TOutput>): Promise<T>

    /**
     * Sends a PATCH request to update a resource by its ID.
     *
     * @param {string} suffix - The endpoint to send the request to.
     * @param {ID} id - The ID of the resource to update.
     * @param {UpdateData<TOutput>} data - The data to be updated.
     * @returns {Promise<TOutput>} A promise that resolves to the updated resource.
     */
    public patch<T = TOutput>(
        suffix: string,
        id: ID,
        data: UpdateData<T>,
    ): Promise<T>

    public patch(
        arg1: string | ID,
        arg2: ID | UpdateData<TOutput>,
        arg3?: UpdateData<TOutput>,
    ) {
        if (arg3) {
            return axiosInstance.patch(`${this.url}/${arg1}/${arg2}`, arg3)
        }

        return axiosInstance.patch(`${this.url}/${arg1}`, arg2)
    }

    /**
     * Sends a PATCH request to the specified endpoint with the provided data.
     *
     * @param id - The ID of the resource to update.
     * @param endpoint - The endpoint to send the request to.
     * @param data - The data to be sent in the request body.
     * @returns A Promise that resolves to the response data.
     *
     * @example
     * ```ts
     * const api = new ApiService('users')
     *
     * api.patchFrom(1, 'profile', { cep: '13405000' }) // url => /v1/users/1/profile
     * ```
     */
    public patchFrom<T = TInput, O = TOutput>(
        id: ID,
        endpoint: string,
        data: UpdateData<T>,
    ): Promise<O> {
        return axiosInstance.patch(`${this.url}/${id}/${endpoint}`, data)
    }

    public post<T = TInput, O = TOutput>(
        suffix: string,
        data: PostData<T>,
    ): Promise<O>

    public post<T = TInput, O = TOutput>(data: PostData<T>): Promise<O>

    public post<T = TInput, O = TOutput>(
        arg1: string | PostData<T>,
        arg2?: PostData<T>,
    ): Promise<O> {
        if (typeof arg1 === 'string') {
            return axiosInstance.post(`${this.url}/${arg1}`, arg2, {
                ...(arg2 instanceof FormData
                    ? {
                          headers: {
                              'Content-Type': 'multipart/form-data',
                          },
                      }
                    : {}),
            })
        }

        return axiosInstance.post(this.url, arg1, {
            ...(arg2 instanceof FormData
                ? {
                      headers: {
                          'Content-Type': 'multipart/form-data',
                      },
                  }
                : {}),
        })
    }

    public postFrom<T = TInput, O = TOutput>(
        id: ID,
        endpoint: string,
        data: PostData<T>,
    ): Promise<O> {
        return axiosInstance.post(`${this.url}/${id}/${endpoint}`, data)
    }

    public put(id: ID, data: UpdateData<TOutput>): Promise<TOutput> {
        return axiosInstance.put(`${this.url}/${id}`, data)
    }

    public async pdf<TBlob extends boolean = true>({
        data,
        queryParam,
        isBlob = true as TBlob,
    }: DownloadData<TInput, TBlob>): Promise<void> {
        const url = mountUrl(`${this.url}/pdf`, queryParam || {})
        return axiosInstance
            .post(url, data, { responseType: isBlob ? 'blob' : 'json' })
            .then((file: any) => {
                if (!isBlob) return this.render(file, 'pdf')

                const newTab = window.open('', '_blank')
                if (!newTab) {
                    alert(
                        'A nova aba foi bloqueada. Habilite pop-ups para abrir o arquivo.',
                    )
                    return
                }

                const blobFile = new Blob([file], { type: 'application/pdf' })
                const fileURL = URL.createObjectURL(blobFile)

                newTab.location.href = fileURL

                newTab.onbeforeunload = () => {
                    URL.revokeObjectURL(fileURL)
                }
            })
            .catch(async (error: any) => {
                if (isBlob && error instanceof Blob) {
                    if (error.type === 'application/json') {
                        const text = await error.text()
                        throw new Error(JSON.parse(text).message)
                    }
                }
                throw error
            })
    }

    public static async fetchPdf({
        queryParams = {},
        route,
        action,
        filename = 'download',
    }: {
        route: string
        queryParams?: Record<string, string>
        action: 'download' | 'open'
        filename?: string
    }): Promise<void> {
        const url = mountUrl(
            `${axiosInstance.defaults.baseURL}/${route}`,
            queryParams,
        )
        if (action === 'download') {
            return axiosInstance
                .get(url, { responseType: 'blob' })
                .then((file: any) => {
                    const blobFile = new Blob([file], {
                        type: 'application/pdf',
                    })
                    const fileURL = URL.createObjectURL(blobFile)

                    const link = document.createElement('a')
                    link.href = fileURL
                    link.download = `${filename}.pdf`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)

                    URL.revokeObjectURL(fileURL)
                })
                .catch(blob => {
                    if (blob.type === 'application/json') {
                        return blob.text().then((text: string) => {
                            throw new Error(JSON.parse(text).message)
                        })
                    }

                    throw blob
                })
        }

        if (action === 'open') {
            return axiosInstance
                .get(url, { responseType: 'blob' })
                .then((file: any) => {
                    const blobFile = new Blob([file], {
                        type: 'application/pdf',
                    })
                    const fileURL = URL.createObjectURL(blobFile)

                    const newTab = window.open('', '_blank')
                    if (!newTab) {
                        alert(
                            'A nova aba foi bloqueada. Habilite pop-ups para abrir o arquivo.',
                        )
                        return
                    }

                    newTab.location.href = fileURL

                    newTab.onbeforeunload = () => {
                        URL.revokeObjectURL(fileURL)
                    }
                })
                .catch(blob => {
                    if (blob.type === 'application/json') {
                        return blob.text().then((text: string) => {
                            throw new Error(JSON.parse(text).message)
                        })
                    }

                    throw blob
                })
        }
    }

    /**
     * Downloads data as a text file.
     *
     * @template TTxt - The type of the data to be downloaded.
     * @param {DownloadData<TTxt>} options - The options for downloading the data.
     * @param {TTxt} options.data - The data to be downloaded.
     * @param {Record<string, string>} [options.queryParam] - The query parameters for the download URL.
     * @returns {Promise<void>} - A promise that resolves when the download is complete.
     */
    public txt<TTxt = TInput>({
        data,
        queryParam,
    }: DownloadData<TTxt>): Promise<void> {
        const url = mountUrl(`${this.url}/txt`, queryParam || {})
        return axiosInstance
            .post(url, data)
            .then((filename: any) => this.render(filename, 'txt'))
    }

    /**
     * Zips the provided data and sends it to the server.
     *
     * @template TZip - The type of data to be zipped.
     * @param {DownloadData<TZip>} options - The options for downloading and zipping the data.
     * @param {TZip} options.data - The data to be zipped.
     * @param {object} [options.queryParam] - The query parameters to be included in the request URL.
     * @returns {Promise<void>} A Promise that resolves when the zip operation is completed successfully.
     */
    public async zip<TZip = TInput, B extends true = true>(
        data: DownloadData<TZip, B>,
    ): Promise<void>
    public async zip<TZip = TInput, TOutput = TInput, B extends false = false>(
        data: DownloadData<TZip, B>,
    ): Promise<TOutput>
    public async zip<TZip = TInput, B extends false | undefined = false>({
        data,
        queryParam,
        isBlob = true as B,
    }: DownloadData<TZip, any>) {
        const url = mountUrl(`${this.url}/zip`, queryParam || {})
        if (isBlob) {
            return axiosInstance
                .post(url, data)
                .then((filename: any) => this.blob(filename))
        }

        return axiosInstance.post(url, data)
    }

    /**
     * Imports data using a multipart/form-data request.
     *
     * @param form - The form data to be sent in the request.
     * @returns A Promise that resolves to the imported data.
     */
    public import<I = TOutput>(form: FormData): Promise<I> {
        return axiosInstance.post(`${this.url}/import`, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    }

    public download(data: PostData<TInput>): Promise<void>

    public download(filename: string): Promise<void>

    public download<Arg1 = string | PostData<TInput>>(
        arg1: Arg1,
    ): Promise<void> {
        if (typeof arg1 === 'string') {
            return this.blob(arg1)
        }

        return axiosInstance.post(`${this.url}/download`, arg1, {
            responseType: 'blob',
        })
    }

    public useFetch<T = TOutput[]>(
        options?: UseFetchOptions<TOutput, TParams>,
    ): UseQueryResult<T>

    public useFetch<T = TOutput>(
        isArray: false,
        options?: UseFetchOptions<TOutput, TParams>,
    ): UseQueryResult<T>

    public useFetch<T = TOutput[]>(
        arg1?: boolean | UseFetchOptions<TOutput, TParams>,
        arg2?: UseFetchOptions<TOutput, TParams>,
    ) {
        if (typeof arg1 === 'boolean') {
            const query = arg2?.queryParams || ({} as TParams)
            const queryKey = this.queryKey(query)

            const url = mountUrl(this.url, query as QueryParams)

            // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
            return useQuery(
                queryKey,
                ({ signal }) => {
                    return axiosInstance.get(url, {
                        signal,
                    })
                },
                {
                    ...this.options,
                    ..._omit(arg2, ['queryParams']),
                },
            ) as UseQueryResult<T>
        } else {
            const query = arg1?.queryParams || ({} as TParams)
            const queryKey = this.queryKey(query)

            const url = mountUrl(this.url, query as QueryParams)

            // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
            return useQuery(
                queryKey,
                ({ signal }) => {
                    return axiosInstance.get(url, {
                        signal,
                    })
                },
                {
                    ...this.options,
                    ..._omit(arg1, ['queryParams']),
                },
            ) as UseQueryResult<T[]>
        }
    }

    public useFetchById<T = TOutput[]>(
        id: ID,
        isArray: true,
        options?: UseFetchOptions<TOutput, TParams>,
    ): UseQueryResult<T>

    public useFetchById<T = TOutput>(
        id: ID,
        options?: UseFetchOptions<TOutput, TParams>,
    ): UseQueryResult<T>

    public useFetchById<T = TOutput>(
        arg1: number,
        arg2?: UseFetchOptions<TOutput, TParams> | boolean,
        arg3?: UseFetchOptions<TOutput, TParams>,
    ) {
        if (typeof arg2 === 'boolean') {
            const query = arg3?.queryParams || ({} as TParams)
            const queryKey = this.queryKey({ id: arg1, ...query })

            const url = mountUrl(`${this.url}/${arg1}`, query as QueryParams)

            // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
            return useQuery(
                queryKey,
                ({ signal }) => {
                    return axiosInstance.get(url, {
                        signal,
                    })
                },
                {
                    ...this.options,
                    ..._omit(arg3, ['queryParams']),
                },
            ) as UseQueryResult<T>
        } else {
            const query = arg2?.queryParams || ({} as TParams)
            const queryKey = this.queryKey({ id: arg1, ...query })

            const url = mountUrl(`${this.url}/${arg1}`, query as QueryParams)

            // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
            return useQuery(
                queryKey,
                ({ signal }) => {
                    return axiosInstance.get(url, {
                        signal,
                    })
                },
                {
                    ...this.options,
                    ..._omit(arg2, ['queryParams']),
                },
            ) as UseQueryResult<T>
        }
    }

    public useFetchByIdFrom<T>(
        id: ID,
        endpoint: string,
        options?: UseFetchOptions<TOutput, TParams>,
    ) {
        const queryParams = options?.queryParams || ({} as TParams)
        const queryKey = this.queryKey({
            id,
            endpoint,
            ...queryParams,
        } as TParams)

        const url = mountUrl(
            `${this.url}/${id}/${endpoint}`,
            queryParams as QueryParams,
        )

        // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
        return useQuery(
            queryKey,
            ({ signal }) => {
                return axiosInstance.get(url, {
                    signal,
                })
            },

            {
                ...this.options,
                ..._omit(options, ['queryParams']),
                enabled:
                    this.options?.enabled ||
                    options?.enabled ||
                    (typeof id === 'number' && id > 0) ||
                    (typeof id === 'string' && id.length > 0),
            },
        ) as UseQueryResult<T>
    }

    public useInfiniteScroll<T = TOutput>(
        options?: UseInfiniteQueryOptions<T, TParams>,
    ): UseInfiniteQueryResult<PaginatedResponse<T>> {
        const perPage = options?.perPage ?? 25
        const query = options?.queryParams || ({} as TParams)
        const queryKey = this.queryKey({
            ...query,
            infinite: true,
        } as TParams)

        // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
        return useInfiniteQuery(
            queryKey,
            ({ pageParam = 1, signal }) => {
                const url = mountUrl(this.url, {
                    ...(query as QueryParams),
                    page: pageParam,
                    perPage,
                })
                return axiosInstance.get(url, { signal })
            },
            {
                getNextPageParam: (lastPage: PaginatedResponse<T>) =>
                    lastPage.meta.currentPage < lastPage.meta.lastPage
                        ? lastPage.meta.currentPage + 1
                        : undefined,
                enabled: options?.enabled,
                keepPreviousData: options?.keepPreviousData,
                refetchOnWindowFocus: false,
                refetchInterval: false,
                refetchIntervalInBackground: false,
                refetchOnMount: false,
                refetchOnReconnect: false,
            },
        )
    }

    public async file(filename: string) {
        return this.blob(filename)
    }

    private async blob(filename: string) {
        return fetch(
            axiosInstance.defaults.baseURL + `${this.url}/${filename}`,
            {
                headers: {
                    Authorization: `Bearer ${Cookies.get('JWT')}`,
                },
            },
        )
            .then(response => response.blob())
            .then(blob => {
                if (
                    filename.startsWith('tmp/') ||
                    filename.startsWith('zip/')
                ) {
                    filename = filename.slice(4)
                }

                const url = window.URL.createObjectURL(new Blob([blob]))
                const link = document.createElement('a')

                link.href = url
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()

                link.parentNode?.removeChild(link)

                window.URL.revokeObjectURL(url)
            })
    }

    private render(filename: string, tipo: 'pdf' | 'txt') {
        window.open(
            (
                axiosInstance.defaults.baseURL +
                `${this.url}/${tipo}/${filename}`
            ).replace(/([^:]\/)\/+/g, '$1'),
            '_blank',
        )
    }
}
